import React from 'react'
import styled, {
  elementProps,
  labelWhenSmallProps,
} from '../../constants/theme'
import {
  BasicType,
  ElementType,
  FormElementProps,
  QuestionType,
} from './FormElementParts'

const TEL_ALLOWED_CHARS = /[^\d().\-\s]/
const NUMBERS = /[^\d]/

export const formattedValue = (
  type: BasicType | ElementType | QuestionType,
  value: string,
): string => {
  if (type !== ElementType.TEL) {
    return value
  }

  const areaCode = value.slice(0, 3)
  const firstThree = value.slice(3, 6)
  const lastFour = value.slice(6, 10)

  if (lastFour.length > 0) {
    return `(${areaCode}) ${firstThree}-${lastFour}`
  } else if (firstThree.length > 0) {
    return `(${areaCode}) ${firstThree}`
  } else if (areaCode.length > 0) {
    return `(${areaCode}`
  } else {
    return ''
  }
}

export const getAttributeType = (
  type: BasicType | ElementType | QuestionType,
): string => {
  switch (type) {
    case ElementType.ZIP:
      return ElementType.TEL
    default:
      return type
  }
}

export const storedValue = (
  type: BasicType | ElementType | QuestionType,
  value: string,
): string => {
  switch (type) {
    case ElementType.TEL:
      return value
        .split(NUMBERS)
        .join('')
        .slice(0, 10)
    case ElementType.ZIP:
      return value
        .split(NUMBERS)
        .join('')
        .slice(0, 5)
    default:
      return value
  }
}

export const allowedInputValue = (
  type: BasicType | ElementType | QuestionType,
  value: string,
): string => {
  switch (type) {
    case ElementType.TEL:
      return value.split(TEL_ALLOWED_CHARS).join('')
    case ElementType.ZIP:
      return value
        .split(NUMBERS)
        .join('')
        .slice(0, 5)
    default:
      return value
  }
}

interface State {
  value: string
}

class InputElement extends React.Component<FormElementProps, State> {
  constructor(props) {
    super(props)
    this.state = {
      value: formattedValue(props.type, props.value || ''),
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.value && prevProps.value !== this.props.value) {
      this.setState({ value: this.props.value })
    }
  }

  handleChange = e => {
    e.preventDefault()

    const { onChange, name, type } = this.props
    const newValue = e.target.value
    this.setState({ value: allowedInputValue(type, newValue) })

    if (onChange) {
      onChange({ name, value: storedValue(type, newValue) })
    }
  }

  handleBlur = () => {
    const { type } = this.props
    this.setState({
      value: formattedValue(type, storedValue(type, this.state.value)),
    })
  }

  render() {
    const {
      disabled,
      isFocused,
      isRequired,
      name,
      onInput,
      onKeyUp,
      onPaste,
      placeholder,
      type,
      unifiedErrorText,
    } = this.props

    const { value } = this.state
    const className = value.length > 0 ? 'HasValue' : ''
    if (!name) {
      return null
    }
    const isInvalid = !!(unifiedErrorText && unifiedErrorText.length > 0)
    /* eslint-disable */
    return (
      <Input
        aria-describedby={isInvalid ? `${name}Error` : undefined}
        aria-invalid={isInvalid}
        autoFocus={isFocused}
        className={className}
        disabled={disabled}
        id={name}
        name={name}
        onBlur={this.handleBlur}
        onChange={this.handleChange}
        onInput={onInput}
        onKeyUp={onKeyUp}
        onPaste={onPaste}
        placeholder={placeholder || undefined}
        required={isRequired}
        type={getAttributeType(type)}
        value={value}
      />
    )
    /* eslint-enable */
  }
}
const Input = styled.input(
  {
    '&:-webkit-autofill': {
      transition: 'background-color 5000s ease-in-out 0s',
    },
    '&:disabled': {
      cursor: 'not-allowed',
    },
    '&:focus ~ label, &:disabled ~ label, &.HasValue ~ label': {
      ...labelWhenSmallProps,
    },
    '&[type="date"]': {
      paddingRight: 0,
    },
    ...elementProps,
    boxShadow: 'unset', // kills red around 'required' inputs in FF
  },
  ({ theme }) => ({
    '.invalid &': {
      color: theme.error,
      fontWeight: 'bold',
    },
    color: theme.text,
    fontFamily: theme.fontFamily,
  }),
)
Input.displayName = 'Input'

export default InputElement
